/** Devour Corpses: the Dune Devourers's meal, and the strikes it buys.
 *
 * The expansion: instead of attacking or moving, an Dune Devourers may eat any corpse on
 * the field within its movement - undead, unliving and war machines included.
 * Each meal banks one extra strike on its next attack, they stack without
 * limit, and the devoured corpse can no longer be interacted with.
 *
 * **Two deliberate departures, both because of what this kernel does and does
 * not have.**
 *
 * 1. The expansion's headline consequence is that a devoured corpse is denied to
 *    Resurrection and Summon Demons. **There is nothing here to deny.**
 *    `resolveTargets` refuses a dead stack outright (`STACK_DEAD`) and the
 *    `RESURRECT` effect begins `if (!isAlive(st, slot)) return false`, so no
 *    spell in this game raises the dead. The corpse is still marked eaten, so
 *    the denial is *ready* the day a raise-the-dead spell exists - but claiming
 *    the rule works today would be claiming a guard on a path nobody walks.
 * 2. Nothing is stored. A corpse carries a marker modifier and the devourer
 *    carries one per banked strike, counted the way `runes.ts` counts levels.
 *    No `StackStore` column, no save-format change, and `undoTo` covers both
 *    for free because the journal already covers `mods`.
 */

import type { ContentRegistry } from "../content/registry.js";
import type { Modifier } from "../modifiers.js";
import { asModifierSrcId } from "../ids.js";
import type { BattleState } from "./state.js";
import { F_ALIVE } from "./state.js";
import { abilitiesOf } from "./abilities.js";
import { addMod, removeModsBySource, type Ctx } from "./apply.js";

/** On the corpse: this one has been eaten. */
export const SRC_DEVOURED = asModifierSrcId("battle.devoured");
/** On the devourer: one per banked strike, so the count is the bank. */
const SRC_BANK_PREFIX = "battle.devour.";

const hasSource = (s: BattleState, slot: number, source: string): boolean => {
  for (const m of s.stacks.mods[slot] ?? []) if (m.source === source) return true;
  return false;
};

/** Can this creature eat corpses at all? */
export function isDevourer(content: ContentRegistry, s: BattleState, slot: number): boolean {
  for (const a of abilitiesOf(content, s.stacks.def[slot]!)) {
    if (a.kind === "DEVOUR_CORPSES") return true;
  }
  return false;
}

/** Already eaten - and therefore, when anything can raise the dead, gone. */
export const isDevoured = (s: BattleState, slot: number): boolean =>
  hasSource(s, slot, SRC_DEVOURED as string);

/** Extra strikes this stack has banked. Unlimited by design a reference screenshot */
export function bankedStrikes(s: BattleState, slot: number): number {
  let n = 0;
  for (const m of s.stacks.mods[slot] ?? []) {
    if ((m.source as string).startsWith(SRC_BANK_PREFIX)) n++;
  }
  return n;
}

/**
 * Is `corpse` a meal for `eater`?
 *
 * Deliberately permissive about WHAT may be eaten - "undead, unliving and war
 * machines included" is the expansion's own list, which is everything - and strict about
 * whether it is there to eat: dead, not already eaten, and not the eater.
 */
export function canDevour(
  content: ContentRegistry, s: BattleState, eater: number, corpse: number,
): boolean {
  if (eater === corpse) return false;
  if (eater < 0 || eater >= s.stacks.n || corpse < 0 || corpse >= s.stacks.n) return false;
  if ((s.stacks.flags[eater]! & F_ALIVE) === 0) return false;
  if (!isDevourer(content, s, eater)) return false;
  // A corpse is a stack that is not alive. Summoned phantoms leave none - they
  // lapse rather than die - but that is `lapseConjured`'s business, not this
  // file's: anything the store still holds with the flag clear is a body.
  if ((s.stacks.flags[corpse]! & F_ALIVE) !== 0) return false;
  return !isDevoured(s, corpse);
}

/** Eat it: the corpse is spent and the eater banks one strike. */
export function devour(c: Ctx, eater: number, corpse: number): void {
  const mark: Modifier = {
    source: SRC_DEVOURED, stat: "attack", op: "add", value: 0,
    expires: { kind: "battle" },
  };
  addMod(c, corpse, mark);
  addMod(c, eater, {
    source: asModifierSrcId(`${SRC_BANK_PREFIX}${bankedStrikes(c.s, eater) + 1}`),
    stat: "attack", op: "add", value: 0, expires: { kind: "battle" },
  });
}

/**
 * Spend one banked strike, if there is one. Returns whether it was spent.
 *
 * The bank is drained one at a time by `resolveAttack` after the defender has
 * had its retaliation, which is the expansion's order: "after a normal attack, if the
 * target is still alive it retaliates, and then bonus strikes start".
 */
export function spendBankedStrike(c: Ctx, slot: number): boolean {
  const n = bankedStrikes(c.s, slot);
  if (n <= 0) return false;
  // Through `removeModsBySource`, never by writing `mods` directly: every
  // change to a stack has to go through the journal or `undoTo` cannot replay
  // it, and a bonus strike that survives an undo is a duplicated attack.
  removeModsBySource(c, slot, `${SRC_BANK_PREFIX}${n}`);
  return true;
}
